/*
 * Bloembollen keuzehulp (YAR-167, rebuilt into a calculator with suggestions
 * YAR-264)
 *
 * Vanilla custom element <bulb-calculator>. It reads the product data that
 * sections/yar-bulb-calculator.liquid emits as JSON, classifies every product
 * into one or more genera, and answers two questions:
 *
 *   1. how many bulbs fit in this spot (one number);
 *   2. which four products suit that spot, and how many packs of each.
 *
 * There are no steps any more (YAR-264): the old variety list, the selection
 * bar, the share-the-area maths, the season picker and the roomy/full
 * preference are gone. The customer fills in the form on the left and reads
 * the answer plus the suggestions on the right.
 *
 * All customer facing text comes from the locale files through the i18n JSON
 * block in the section, never from this file.
 */
(function () {
  'use strict';

  /* =====================================================================
   * TUNING BLOCK
   * Everything Jop may want to tune lives here: the bulbs per m2 rules, the
   * title and description patterns that decide which genus a product is, the
   * Latin genus mapping and the popularity scores that order the suggestions.
   * Nothing below this block needs changing to add a new product: give it a
   * genus metafield or a recognisable title and it is classified
   * automatically.
   *
   * Rule fields:
   *   full        bulbs per m2 for a full bed of bulbs
   *   between     bulbs per m2 between existing plants
   *   lawn        bulbs per m2 in grass (null = not suitable)
   *   hedge       bulbs per m2 under a hedge or deciduous trees (null = not suitable)
   *   sun         allowed light levels, used only when the product has no
   *               ideal_planting_location metafield
   *   season      spring or summer, used only when the product has no
   *               flowering_month metafield. Kept for the classification, the
   *               UI no longer asks for a season (YAR-264)
   *   naturalises whether the bulbs come back every year (info badge)
   *   spreads     whether the bulbs naturalise, i.e. multiply into drifts over the years (info badge)
   * ===================================================================== */

  var BULB_RULES = {
    tulip: { full: 40, between: 20, lawn: 25, hedge: null, sun: ['zon', 'halfschaduw'], season: 'spring', naturalises: false, spreads: false },
    narcis: { full: 50, between: 20, lawn: 35, hedge: 30, sun: ['zon', 'halfschaduw'], season: 'spring', naturalises: true, spreads: true },
    crocus: { full: 100, between: 25, lawn: 75, hedge: 60, sun: ['zon', 'halfschaduw'], season: 'spring', naturalises: true, spreads: true },
    allium: { full: 25, between: 5, lawn: null, hedge: null, sun: ['zon', 'halfschaduw'], season: 'spring', naturalises: true, spreads: false },
    allium_small: { full: 50, between: 25, lawn: null, hedge: null, sun: ['zon'], season: 'spring', naturalises: true, spreads: true },
    hyacint: { full: 50, between: 20, lawn: null, hedge: null, sun: ['zon', 'halfschaduw'], season: 'spring', naturalises: false, spreads: false },
    galanthus: { full: 100, between: 35, lawn: 60, hedge: 75, sun: ['halfschaduw', 'schaduw', 'zon'], season: 'spring', naturalises: true, spreads: true },
    leucojum: { full: 60, between: 30, lawn: 40, hedge: 40, sun: ['zon', 'halfschaduw'], season: 'spring', naturalises: true, spreads: true },
    muscari: { full: 100, between: 40, lawn: 60, hedge: 60, sun: ['zon', 'halfschaduw'], season: 'spring', naturalises: true, spreads: true },
    fritillaria: { full: 60, between: 25, lawn: 30, hedge: 40, sun: ['halfschaduw', 'schaduw'], season: 'spring', naturalises: true, spreads: true },
    anemone: { full: 60, between: 25, lawn: null, hedge: 40, sun: ['halfschaduw', 'zon'], season: 'spring', naturalises: true, spreads: true },
    camassia: { full: 30, between: 10, lawn: 20, hedge: null, sun: ['zon', 'halfschaduw'], season: 'spring', naturalises: true, spreads: true },
    scilla: { full: 100, between: 40, lawn: 60, hedge: 75, sun: ['halfschaduw', 'zon'], season: 'spring', naturalises: true, spreads: true },
    chionodoxa: { full: 100, between: 40, lawn: 60, hedge: 60, sun: ['zon', 'halfschaduw'], season: 'spring', naturalises: true, spreads: true },
    eranthis: { full: 100, between: 40, lawn: 60, hedge: 75, sun: ['halfschaduw', 'schaduw'], season: 'spring', naturalises: true, spreads: true },
    iris: { full: 80, between: 30, lawn: null, hedge: null, sun: ['zon'], season: 'spring', naturalises: false, spreads: false },
    dahlia: { full: 4, between: 2, lawn: null, hedge: null, sun: ['zon'], season: 'summer', naturalises: false, spreads: false },
    lilium: { full: 12, between: 5, lawn: null, hedge: null, sun: ['zon', 'halfschaduw'], season: 'summer', naturalises: true, spreads: false },
    gladiolus: { full: 30, between: 10, lawn: null, hedge: null, sun: ['zon'], season: 'summer', naturalises: false, spreads: false },
    crocosmia: { full: 9, between: 5, lawn: null, hedge: null, sun: ['zon', 'halfschaduw'], season: 'summer', naturalises: true, spreads: true },
    begonia: { full: 12, between: 6, lawn: null, hedge: null, sun: ['halfschaduw'], season: 'summer', naturalises: false, spreads: false },
    canna: { full: 4, between: 2, lawn: null, hedge: null, sun: ['zon'], season: 'summer', naturalises: false, spreads: false },
    zantedeschia: { full: 9, between: 4, lawn: null, hedge: null, sun: ['zon', 'halfschaduw'], season: 'summer', naturalises: false, spreads: false },
    ranunculus: { full: 40, between: 15, lawn: null, hedge: null, sun: ['zon'], season: 'summer', naturalises: false, spreads: false },
    liatris: { full: 25, between: 10, lawn: null, hedge: null, sun: ['zon'], season: 'summer', naturalises: true, spreads: false },
    colchicum: { full: 30, between: 10, lawn: 20, hedge: 20, sun: ['zon', 'halfschaduw'], season: 'summer', naturalises: true, spreads: true },
    cyclamen: { full: 40, between: 15, lawn: null, hedge: 30, sun: ['halfschaduw', 'schaduw'], season: 'summer', naturalises: true, spreads: true },
    generic: { full: 40, between: 20, lawn: null, hedge: null, sun: ['zon', 'halfschaduw'], season: 'spring', naturalises: false, spreads: false }
  };

  /* Allium is split by name and height: the small drumstick types are planted
   * far denser than the tall ornamental onions. */
  var ALLIUM_SMALL_TITLE = /sphaerocephalon|trommelstok|kogellook|moly|oreophilum|cernuum|flavum|millenium|karataviense|unifolium|roseum|neapolitanum|caeruleum|nigrum|schubertii|christophii|drumstick/i;
  var ALLIUM_SMALL_MAX_HEIGHT = 60;

  /* Allium aflatunense (Purple Sensation and friends) is planted denser than
   * the other tall alliums: 7 to 15 between perennials, 30 to 40 for a full bed. */
  var ALLIUM_TALL_TITLE = /purple sensation|aflatunense|hollandicum/i;
  var ALLIUM_TALL_RULE = { full: 35, between: 10, lawn: null, hedge: null };

  var GENUS_PATTERNS = [
    ['tulip', /tulp|tulip|tulipa/i],
    ['narcis', /narcis|narzisse|daffodil/i],
    ['crocus', /krokus|crocus/i],
    ['allium', /sierui|allium|zierlauch|kogellook|trommelstok|ornamental onion/i],
    ['hyacint', /hyacint|hyazinth|hyacinth(?! ?grape)/i],
    ['galanthus', /sneeuwklok|galanthus|snowdrop|schneegl/i],
    ['leucojum', /lenteklok|leucojum|snowflake|märzenbecher|marzenbecher/i],
    ['muscari', /druifje|muscari|grape hyacinth|traubenhyazinthe/i],
    ['fritillaria', /kievitsbloem|fritillaria|fritillary|schachbrett/i],
    ['anemone', /anemoon|anemone/i],
    ['camassia', /camassia|indianenbloem|prairielelie|quamash/i],
    ['scilla', /scilla|sterhyacint|blaustern|squill/i],
    ['chionodoxa', /chionodoxa|sneeuwroem|glory of the snow|schneeglanz/i],
    ['eranthis', /eranthis|winterakoniet|winterling|winter aconite/i],
    ['iris', /\biris\b/i],
    ['dahlia', /dahlia/i],
    ['lilium', /\blelie|lilium|\blily|\blilie/i],
    ['gladiolus', /gladiol/i],
    ['crocosmia', /crocosmia|montbretia/i],
    ['begonia', /begonia/i],
    ['canna', /\bcanna\b/i],
    ['zantedeschia', /zantedeschia|\bcalla\b|aronskelk/i],
    ['ranunculus', /ranonkel|ranunculus/i],
    ['liatris', /liatris/i],
    ['colchicum', /colchicum|herfsttijloos/i],
    ['cyclamen', /cyclamen/i]
  ];

  /* "grape hyacinth" and "Traubenhyazinthe" are muscari, not hyacint. Both
   * phrases are removed from the text before the hyacint pattern is tested. */
  var MUSCARI_PHRASES = /grape hyacinth|traubenhyazinthe/gi;

  var LATIN_GENUS = {
    tulipa: 'tulip',
    narcissus: 'narcis',
    crocus: 'crocus',
    allium: 'allium',
    hyacinthus: 'hyacint',
    galanthus: 'galanthus',
    leucojum: 'leucojum',
    muscari: 'muscari',
    fritillaria: 'fritillaria',
    anemone: 'anemone',
    camassia: 'camassia',
    scilla: 'scilla',
    chionodoxa: 'chionodoxa',
    eranthis: 'eranthis',
    iris: 'iris',
    dahlia: 'dahlia',
    lilium: 'lilium',
    gladiolus: 'gladiolus',
    crocosmia: 'crocosmia',
    begonia: 'begonia',
    canna: 'canna',
    zantedeschia: 'zantedeschia',
    ranunculus: 'ranunculus',
    liatris: 'liatris',
    colchicum: 'colchicum',
    cyclamen: 'cyclamen',
    agapanthus: 'agapanthus',
    eucomis: 'eucomis',
    freesia: 'freesia',
    nerine: 'nerine',
    hippeastrum: 'generic'
  };

  /* Package products list their species in the first paragraph of the product
   * text. Only that part is scanned: everything from the first heading onwards
   * is about flowering time and planting. The headings are matched with their
   * capital letter so the word "bloemen" inside a normal sentence does not cut
   * the intro short. */
  var INTRO_HEADINGS = /\b(Bloeitijden|Bloeitijd|Bloeiwijze|Bloemen|Aanplant|Tips|Flowering|Planting|Bl(?:ü|u)tezeit|Pflanz)/;

  /* The count and the unit word are not always next to each other: titles like
   * "... - 36 blauwe bloembollen" put a colour or another describing word in
   * between. Up to two such words are allowed, lazily, so a title that names a
   * size first ("12 cm potmaat 50 bollen") still lands on the real count. */
  var PACK_SIZE_PATTERN = /(\d+)\s*(?:[^\d\s]+\s+){0,2}?(bloembollen|bollen|stuks|tulpenbollen|bulbs|zwiebeln|blumenzwiebeln|stück|stuck|pcs|pieces)\b/i;

  var MONTH_NUMBERS = {
    januari: 1, january: 1, januar: 1,
    februari: 2, february: 2, februar: 2,
    maart: 3, march: 3, 'märz': 3, marz: 3,
    april: 4,
    mei: 5, may: 5, mai: 5,
    juni: 6, june: 6,
    juli: 7, july: 7,
    augustus: 8, august: 8,
    september: 9,
    oktober: 10, october: 10,
    november: 11,
    december: 12, dezember: 12
  };

  /* A shortfall of up to 20% of one pack is accepted: the densities are ranges. */
  var SHORTFALL_FACTOR = 0.2;
  /* Bulbs taller than this do not belong in a lawn. */
  var LAWN_MAX_HEIGHT = 40;

  /* Reference density (bulbs per m2) for a medium sized bulb such as narcis or
   * tulp, per spot, at the dense end. This drives the one number in the answer
   * card; LOOSE_FACTOR times it is the quieter number below it. */
  var LOCATION_GUIDE = { border_full: 45, border_between: 20, lawn: 35, hedge: 30 };
  var LOOSE_FACTOR = 0.6;

  /* How many suggestions the tool shows, at most. */
  var SUGGESTION_COUNT = 4;

  /* A pack that holds far more bulbs than the spot needs loses points, so a
   * small border is not offered a package of 305 bulbs while a big one still
   * is. Read as: more than 4x the needed number costs 50 points, more than 2x
   * costs 25. Most generous rule first. */
  var OVERSHOOT_PENALTIES = [
    { factor: 4, penalty: 50 },
    { factor: 2, penalty: 25 }
  ];

  /* Needing many separate packs of one variety (a 6 bulb pack for a whole
   * border) costs points too: above this many packs the score drops. */
  var MANY_PACKS = 3;
  var MANY_PACKS_PENALTY = 25;

  /* Populariteit per handle, 0..100, uit GA4 + Shopify sinds 2026-08-01 (stand 2026-09-11).
   * Vernieuwen: yarinde-hq Marketing/scripts/bulb_popularity.py (zie docstring). */
  var POPULARITY = {
    'bloembollen-vlinderpakket-17-90-bloembollen': 100,
    'bloembollen-bordermix-pakket-18-60-bloembollen': 86,
    'bloembollen-bijenpakket-305-bloembollen': 86,
    'sneeuwklokje-galanthus-woronowii-75-bloembollen': 79,
    'gewoon-sneeuwklokje-galanthus-nivalis': 54,
    'bloembol-pakket-sierui-allium-208-bloembollen': 53,
    'lenteklokje-leucojum-30-bloembollen': 47,
    'grootbloemige-krokus-crocus-large-flowered-blue-75-bloembollen': 43,
    'krokus-crocus': 37,
    'trommelstok-sierui-allium-sphaerocephalon-225-bloembollen': 31,
    'sierui-allium-pinball-wizard-6-bloembollen': 31,
    'bloembollen-tulp-pakket-mix-49-bloembollen-roze-en-wit': 31,
    'blauw-druifje-muscari-armeniacum-300-bloembollen': 30,
    'tulipa-greigii-tulpen-mix-30-bloembollen': 29,
    'tulipa-dubbel-laat-upstar-30-tulpenbollen': 28,
    'sierui-allium-aflatunense-purple-sensation-45-bloembollen': 26,
    'krokus-botanisch-mix-kleuren-180-bloembollen': 25,
    'bloembollen-pakket-sierui-allium-30-bollen-paars-roze-en-wit': 24,
    'tulp-blauw-blue-parrot-parkiettulp-36-bloembollen': 18,
    'tulipa-dubbel-laat-sunlover-36-tulpenbollen': 18,
    'bloembollen-mix-2-soorten-rood-en-wit-90-bloembollen': 15,
    'tulpenbollen-pakket-paars-geel-roze': 15,
    'bloembollen-tulp-pakket-mix-58-bloembollen-rood-en-wit': 15,
    'tulp-tulipa': 15,
    'tulpenbollen-rood-30-bloembollen': 14,
    'tulipa-don-quichotte-triumph-tulp-36-bloembollen': 14,
    'fritillaria-meleagris': 13,
    'narcis-trompet-geel-45-bloembollen': 12,
    'tulipa-black-parrot-parkiettulp-36-bloembollen': 11,
    'sierui-allium-gladiator-his-excellence-9-bloembollen': 9,
    'bloembollen-mix-2-soorten-paars-roze-en-wit-90-tulpenbollen': 8,
    'bloembollen-crocus-pakket-mix-140-bloembollen': 8,
    'sierui-allium-mount-everest-9-bloembollen': 8,
    'bloembollen-mix-2-soorten-paars-en-roze-90-tulpenbollen': 8,
    'hyacint-mix-27-bloembollen': 8,
    'oosterse-sterhyacint-scilla-siberica-120-bloembollen': 8,
    'bloembollen-narcis-aanbieding-300-bollen-geel': 4,
    'bloembollen-narcis-mix-300-bollen-wit-en-geel': 4,
    'tulp-tulipa-ice-cream-15-bloembollen': 2,
    'tulipa-queen-of-night-late-tulp-30-bloembollen': 1,
    'bloembollen-hyacint-pakket-mix-48-bloembollen-paars-blauw-geel-en-roze': 1,
    'bloembollen-narcis-pakket-mix-62-bloembollen': 1,
    'narcis-mini-jetfire-75-bollen-geel-oranje': 1,
    'narcis-mini-tete-a-tete-60-bloembollen': 1,
    'bloembollen-tulp-pakket-mix-45-tulpenbollen-paars-en-wit': 1,
    'bloembollen-narcis-pakket-mix-85-bloembollen-wit-en-geel': 1,
    'narcis-trompet-mix-45-bloembollen': 1,
  };

  /* The customer's choices are kept in localStorage for a day, so a return
   * visit (or a page reload) picks up where they left off. URL parameters
   * (?location=, ?product=) still win over the stored state. */
  var STORAGE_KEY = 'yarindeBulbCalculator';
  var STORAGE_TTL_MS = 24 * 60 * 60 * 1000;

  /* ===================== END OF TUNING BLOCK ===================== */

  var LOCATIONS = ['lawn', 'hedge', 'border_between', 'border_full'];
  var SUN_LEVELS = ['zon', 'halfschaduw', 'schaduw'];
  var DENSITY_FIELD = {
    lawn: 'lawn',
    hedge: 'hedge',
    border_between: 'between',
    border_full: 'full'
  };

  /* Height of the sticky site header, kept free above the answer card when the
   * form is completed on a narrow screen. */
  var ANSWER_SCROLL_OFFSET = 90;

  /* The answer is a guideline, so round it to something readable. */
  function roundGuide(value) {
    var whole = Math.round(value);
    return whole > 20 ? Math.round(whole / 5) * 5 : whole;
  }

  /* Roughly how many bulbs fit in this spot: max is the full planting, min the
   * quieter one. Used for the answer card only, never for the per product
   * numbers. */
  function answerRange(location, area) {
    var guide = LOCATION_GUIDE[location] || LOCATION_GUIDE.border_between;
    var max = Math.round(guide * area);
    var min = Math.round(max * LOOSE_FACTOR);
    return { min: roundGuide(min), max: roundGuide(max) };
  }

  /* The density used per product: the rule for this spot. */
  function densityFor(item, location) {
    return item.rules[DENSITY_FIELD[location]];
  }

  function matchGenera(text) {
    if (!text) return [];
    var subject = String(text);
    var withoutMuscariPhrases = subject.replace(MUSCARI_PHRASES, ' ');
    var found = [];
    for (var i = 0; i < GENUS_PATTERNS.length; i += 1) {
      var key = GENUS_PATTERNS[i][0];
      var pattern = GENUS_PATTERNS[i][1];
      var haystack = key === 'hyacint' ? withoutMuscariPhrases : subject;
      if (pattern.test(haystack) && found.indexOf(key) === -1) found.push(key);
    }
    return found;
  }

  function introHead(intro) {
    if (!intro) return '';
    var text = String(intro);
    var match = INTRO_HEADINGS.exec(text);
    return match ? text.slice(0, match.index) : text;
  }

  function alliumKeyFor(product) {
    var title = String(product.title || '');
    var height = Number(product.height) || 0;
    if (ALLIUM_SMALL_TITLE.test(title)) return 'allium_small';
    if (height > 0 && height < ALLIUM_SMALL_MAX_HEIGHT) return 'allium_small';
    return 'allium';
  }

  function classify(product) {
    var genera = [];
    var genus = String(product.genus || '').trim().toLowerCase();

    if (genus) {
      var latinKeys = Object.keys(LATIN_GENUS);
      for (var i = 0; i < latinKeys.length; i += 1) {
        if (genus.indexOf(latinKeys[i]) === 0) {
          genera = [LATIN_GENUS[latinKeys[i]]];
          break;
        }
      }
    }
    if (!genera.length) genera = matchGenera(product.title);
    if (!genera.length) genera = matchGenera(introHead(product.intro));
    if (!genera.length) genera = ['generic'];

    genera = genera.map(function (key) {
      return key === 'allium' ? alliumKeyFor(product) : key;
    });

    var unique = [];
    genera.forEach(function (key) {
      if (unique.indexOf(key) === -1) unique.push(key);
    });

    return { genera: unique, kind: unique.length > 1 ? 'mix' : 'single' };
  }

  function ruleForKey(key, product) {
    var base = BULB_RULES[key] || BULB_RULES.generic;
    var rule = {
      full: base.full,
      between: base.between,
      lawn: base.lawn,
      hedge: base.hedge,
      sun: base.sun.slice(),
      season: base.season,
      naturalises: base.naturalises,
      spreads: base.spreads
    };
    if (key === 'allium' && ALLIUM_TALL_TITLE.test(String(product.title || ''))) {
      rule.full = ALLIUM_TALL_RULE.full;
      rule.between = ALLIUM_TALL_RULE.between;
      rule.lawn = ALLIUM_TALL_RULE.lawn;
      rule.hedge = ALLIUM_TALL_RULE.hedge;
    }
    return rule;
  }

  function mergeRules(rules) {
    if (rules.length === 1) return rules[0];

    var sum = function (field) {
      return rules.reduce(function (acc, rule) {
        return acc + rule[field];
      }, 0);
    };
    var minOrNull = function (field) {
      var values = rules.map(function (rule) {
        return rule[field];
      });
      if (values.indexOf(null) !== -1) return null;
      return Math.min.apply(null, values);
    };

    var sun = SUN_LEVELS.filter(function (level) {
      return rules.every(function (rule) {
        return rule.sun.indexOf(level) !== -1;
      });
    });

    var springCount = rules.filter(function (rule) {
      return rule.season === 'spring';
    }).length;

    return {
      full: Math.round(sum('full') / rules.length),
      between: Math.round(sum('between') / rules.length),
      lawn: minOrNull('lawn'),
      hedge: minOrNull('hedge'),
      sun: sun.length ? sun : ['zon', 'halfschaduw'],
      season: springCount >= rules.length - springCount ? 'spring' : 'summer',
      naturalises: rules.every(function (rule) {
        return rule.naturalises;
      }),
      spreads: rules.every(function (rule) {
        return rule.spreads;
      })
    };
  }

  function rulesForProduct(product) {
    var classified = classify(product);
    var rules = classified.genera.map(function (key) {
      return ruleForKey(key, product);
    });
    return {
      genera: classified.genera,
      kind: classified.kind,
      rules: mergeRules(rules)
    };
  }

  function parsePackSize(title) {
    var match = PACK_SIZE_PATTERN.exec(String(title || ''));
    if (!match) return null;
    var size = parseInt(match[1], 10);
    return size > 0 ? size : null;
  }

  function lightLevelOf(value) {
    var text = String(value || '').toLowerCase();
    if (/half|part|halb/.test(text)) return 'halfschaduw';
    if (/zon|sun|sonne/.test(text)) return 'zon';
    if (/schaduw|shade|schatten/.test(text)) return 'schaduw';
    return null;
  }

  function lightLevelsOf(product, rules) {
    var values = Array.isArray(product.locations) ? product.locations : [];
    var levels = [];
    values.forEach(function (value) {
      var level = lightLevelOf(value);
      if (level && levels.indexOf(level) === -1) levels.push(level);
    });
    return levels.length ? levels : rules.sun.slice();
  }

  /* Sorted, unique month numbers (1..12) from the flowering_month metafield. */
  function bloomMonths(product) {
    var months = Array.isArray(product.months) ? product.months : [];
    var numbers = [];
    months.forEach(function (name) {
      var number = MONTH_NUMBERS[String(name || '').trim().toLowerCase()];
      if (number && numbers.indexOf(number) === -1) numbers.push(number);
    });
    return numbers.sort(function (a, b) { return a - b; });
  }

  /* Spring or summer. The UI no longer asks (every product on sale is a spring
   * bloomer), but the classification keeps the field. */
  function seasonOf(product, rules) {
    var months = Array.isArray(product.months) ? product.months : [];
    var spring = 0;
    var summer = 0;
    months.forEach(function (name) {
      var number = MONTH_NUMBERS[String(name || '').trim().toLowerCase()];
      if (!number) return;
      if (number >= 1 && number <= 6) spring += 1;
      else if (number >= 7 && number <= 11) summer += 1;
    });
    if (!spring && !summer) return rules.season;
    return spring >= summer ? 'spring' : 'summer';
  }

  function enrich(product) {
    var resolved = rulesForProduct(product);
    var packSize = parsePackSize(product.title);
    return {
      raw: product,
      genera: resolved.genera,
      kind: resolved.kind,
      rules: resolved.rules,
      packSize: packSize === null ? 1 : packSize,
      loose: packSize === null,
      lights: lightLevelsOf(product, resolved.rules),
      season: seasonOf(product, resolved.rules)
    };
  }

  /* Returns the reason this product does not suit the spot, or null when it
   * fits. Season and colour no longer play a part (YAR-264). */
  function exclusionReason(item, state) {
    var product = item.raw;
    if (!product.available || !(Number(product.price) > 0)) return 'unavailable';
    if (state.location === 'lawn' && Number(product.height) > LAWN_MAX_HEIGHT) return 'too_tall_lawn';
    if (state.location === 'lawn' && item.rules.lawn === null) return 'not_lawn';
    if (state.location === 'hedge' && item.rules.hedge === null) return 'not_hedge';
    if (item.lights.indexOf(state.sun) === -1) return 'sun';
    return null;
  }

  function computeItem(density, area, packSize, loose, price) {
    var needed = density * area;
    var size = loose ? 1 : packSize;
    var packs = loose
      ? Math.max(1, Math.ceil(needed))
      : Math.max(1, Math.ceil((needed - SHORTFALL_FACTOR * size) / size));
    var bulbs = packs * size;
    return {
      needed: needed,
      packSize: size,
      packs: packs,
      bulbs: bulbs,
      total: packs * price
    };
  }

  /* The group a product belongs to in the suggestion list: one suggestion per
   * group, so the customer never gets four kinds of tulip. The group is the
   * classified genus (a tulip mix is still a tulip); everything that spans
   * several genera or none at all is one group of mixed packages. */
  function groupKeyFor(item) {
    var genera = item.genera || [];
    if (genera.length === 1 && genera[0] !== 'generic') return genera[0];
    return 'pakket';
  }

  function popularityOf(handle) {
    var value = POPULARITY[handle];
    return typeof value === 'number' ? value : 0;
  }

  /* Popularity minus the overshoot penalty (OVERSHOOT_PENALTIES) and the
   * many packs penalty (MANY_PACKS). */
  function suggestionScore(handle, needed, bulbs, packs) {
    var score = popularityOf(handle);
    if (!(needed > 0)) return score;
    for (var i = 0; i < OVERSHOOT_PENALTIES.length; i += 1) {
      if (bulbs > OVERSHOOT_PENALTIES[i].factor * needed) {
        score -= OVERSHOOT_PENALTIES[i].penalty;
        break;
      }
    }
    if (packs > MANY_PACKS) score -= MANY_PACKS_PENALTY;
    return score;
  }

  function asItem(product) {
    return product && product.raw ? product : enrich(product);
  }

  /* Up to SUGGESTION_COUNT products for this spot, best score first and never
   * two from the same group. Accepts raw products or enriched items.
   * state: { location, sun, area, preselect }. Every entry is
   * { item, numbers, group, score }. */
  function pickSuggestions(products, state) {
    if (!state || !state.location || !state.sun || !(state.area > 0)) return [];

    var candidates = [];
    (products || []).forEach(function (product) {
      var item = asItem(product);
      if (exclusionReason(item, state)) return;
      var density = densityFor(item, state.location);
      if (!(density > 0)) return;
      var numbers = computeItem(density, state.area, item.packSize, item.loose, Number(item.raw.price));
      candidates.push({
        item: item,
        numbers: numbers,
        group: groupKeyFor(item),
        score: suggestionScore(item.raw.handle, numbers.needed, numbers.bulbs, numbers.packs)
      });
    });

    var byScore = function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      if (a.numbers.total !== b.numbers.total) return a.numbers.total - b.numbers.total;
      return a.item.raw.handle < b.item.raw.handle ? -1 : 1;
    };
    candidates.sort(byScore);

    var picked = [];
    var groups = [];

    /* A customer coming from a product page sees that product first, as long
     * as it suits the spot. */
    var pinned = null;
    if (state.preselect) {
      candidates.forEach(function (entry) {
        if (pinned || entry.item.raw.handle !== state.preselect) return;
        pinned = entry;
        picked.push(entry);
        groups.push(entry.group);
      });
    }

    candidates.forEach(function (entry) {
      if (picked.indexOf(entry) !== -1) return;
      if (picked.length >= SUGGESTION_COUNT || groups.indexOf(entry.group) !== -1) return;
      picked.push(entry);
      groups.push(entry.group);
    });

    /* Fewer groups than SUGGESTION_COUNT (full shade, say) means fewer
     * suggestions: a second snowdrop next to the first reads as a duplicate.
     * A product the customer came in with stays first. */
    if (pinned) picked.shift();
    picked.sort(byScore);
    if (pinned) picked.unshift(pinned);

    return picked.slice(0, SUGGESTION_COUNT);
  }

  function translate(dictionary, key, vars) {
    var value = key.split('.').reduce(function (acc, part) {
      return acc && typeof acc === 'object' ? acc[part] : undefined;
    }, dictionary);
    if (typeof value !== 'string') return '';
    if (!vars) return value;
    return Object.keys(vars).reduce(function (text, name) {
      return text.replace(new RegExp('\\{\\{\\s*' + name + '\\s*\\}\\}', 'g'), vars[name]);
    }, value);
  }

  if (typeof HTMLElement !== 'undefined' && typeof customElements !== 'undefined') {
    class BulbCalculator extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;

        this.sectionId = this.dataset.sectionId;
        this.i18n = this.readJson('BulbCalcI18n-' + this.sectionId) || {};
        var products = this.readJson('BulbCalcData-' + this.sectionId) || [];
        this.items = products.map(enrich);
        this.sellableCount = this.items.filter(function (item) {
          return item.raw.available && Number(item.raw.price) > 0;
        }).length;

        this.setupFormatters();
        this.cacheElements();
        this.setupState();
        this.bindEvents();
        this.renderCollectionLink();
        this.render();
      }

      readJson(id) {
        var node = this.querySelector('#' + id);
        if (!node) return null;
        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          console.error('[bulb-calculator] could not parse', id, error);
          return null;
        }
      }

      t(key, vars) {
        return translate(this.i18n, key, vars);
      }

      setupFormatters() {
        var locale = this.dataset.locale || undefined;
        var currency = this.dataset.currency || 'EUR';
        var makeFormatter = function (options) {
          try {
            return new Intl.NumberFormat(locale, options);
          } catch (error) {
            return new Intl.NumberFormat(undefined, options);
          }
        };
        this.numberFormat = makeFormatter();
        this.decimalFormat = makeFormatter({ minimumFractionDigits: 1, maximumFractionDigits: 1 });
        this.moneyFormat = makeFormatter({ style: 'currency', currency: currency });
      }

      formatNumber(value) {
        return this.numberFormat.format(value);
      }

      formatArea(value) {
        var rounded = Math.round(value * 10) / 10;
        return Number.isInteger(rounded) ? this.formatNumber(rounded) : this.decimalFormat.format(rounded);
      }

      formatMoney(cents) {
        return this.moneyFormat.format((Number(cents) || 0) / 100);
      }

      cacheElements() {
        this.areaInput = this.querySelector('[data-bulb-area]');
        this.lengthInput = this.querySelector('[data-bulb-length]');
        this.widthInput = this.querySelector('[data-bulb-width]');
        this.locationHelp = this.querySelector('[data-bulb-location-help]');
        this.lengthLabel = this.querySelector('[data-bulb-length-label]');
        this.widthLabel = this.querySelector('[data-bulb-width-label]');
        this.tipBlocks = this.querySelectorAll('[data-bulb-tips-for]');
        this.answerEl = this.querySelector('[data-bulb-answer]');
        this.answerHeading = this.querySelector('[data-bulb-answer-heading]');
        this.answerMin = this.querySelector('[data-bulb-answer-min]');
        this.answerPrompt = this.querySelector('[data-bulb-answer-prompt]');
        this.answerBody = this.querySelector('[data-bulb-answer-body]');
        this.suggestionsEl = this.querySelector('[data-bulb-suggestions]');
        this.suggestionGrid = this.querySelector('[data-bulb-suggestion-grid]');
        this.emptyState = this.querySelector('[data-bulb-empty]');
        this.collectionLink = this.querySelector('[data-bulb-collection-link]');
        this.cardTemplate = this.querySelector('[data-bulb-suggestion-template]');
        this.badgeTemplate = this.querySelector('[data-bulb-badge-template]');
      }

      setupState() {
        var params = new URLSearchParams(window.location.search);
        var location = params.get('location');
        var defaultArea = parseFloat(this.dataset.defaultArea);

        this.hedgeStrip = parseFloat(this.dataset.hedgeStrip) || 0.5;
        this.preselectHandle = params.get('product') || '';

        /* Nothing is preselected: the customer picks a spot and a sun level
         * first. Only ?location= presets the spot. */
        this.state = {
          location: LOCATIONS.indexOf(location) !== -1 ? location : null,
          sun: null,
          area: null,
          preselect: ''
        };

        var saved = this.loadState();
        if (saved) this.applySavedState(saved);
        /* ?location= wins over whatever was stored. */
        if (LOCATIONS.indexOf(location) !== -1) this.state.location = location;

        if (this.preselectHandle) {
          var known = this.items.some(function (item) {
            return item.raw.handle === this.preselectHandle;
          }, this);
          if (known) this.state.preselect = this.preselectHandle;
          /* A customer coming from a product page must see an answer straight
           * away, so fall back to a spot, a sun level and an area. */
          if (!this.state.location) this.state.location = 'border_between';
          if (!this.state.sun) this.state.sun = 'zon';
          if (!(this.state.area > 0)) this.state.area = defaultArea > 0 ? defaultArea : 2;
        }

        this.syncLocationInputs();
        this.syncAreaInput();
        this.syncSunButtons();
        if (saved) {
          if (this.lengthInput && saved.length) this.lengthInput.value = saved.length;
          if (this.widthInput && saved.width) this.widthInput.value = saved.width;
        }

        /* A restored session already has its answer, so the first render must
         * not scroll anywhere. */
        this.wasReady = this.isReady();
        this.track('bulb_calc_view', { bulb_from_product: this.preselectHandle || '' });
      }

      /* Everything the answer and the suggestions need: a spot, a sun level and
       * an area. Until then the answer card shows a prompt. */
      isReady() {
        return !!(this.state.location && this.state.sun && this.state.area > 0);
      }

      /* Below lg, the same breakpoint as the CSS. */
      isNarrow() {
        return typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 1023px)').matches;
      }

      /* On a narrow screen the form sits above the answer, so bring the answer
       * card into view the moment the form is complete. */
      scrollToAnswer() {
        if (!this.answerEl || typeof this.answerEl.getBoundingClientRect !== 'function') return;
        if (typeof window.scrollTo !== 'function') return;
        var offset = window.pageYOffset || document.documentElement.scrollTop || 0;
        var top = this.answerEl.getBoundingClientRect().top + offset - ANSWER_SCROLL_OFFSET;
        try {
          window.scrollTo({ top: top > 0 ? top : 0, behavior: 'smooth' });
        } catch (error) {
          window.scrollTo(0, top > 0 ? top : 0);
        }
      }

      bindEvents() {
        var self = this;

        this.querySelectorAll('[data-bulb-location]').forEach(function (input) {
          input.addEventListener('change', function () {
            self.state.location = input.value;
            self.syncLocationInputs();
            self.updateHelperLabels();
            self.render();
          });
        });

        this.querySelectorAll('[data-bulb-sun]').forEach(function (button) {
          button.addEventListener('click', function () {
            self.state.sun = button.dataset.bulbSun;
            self.syncSunButtons();
            self.render();
          });
        });

        if (this.areaInput) {
          this.areaInput.addEventListener('input', function () {
            var value = parseFloat(self.areaInput.value);
            self.state.area = value > 0 ? value : null;
            self.render();
          });
        }

        var recalcArea = function () {
          var length = self.numberIn(self.lengthInput);
          var width = self.numberIn(self.widthInput);
          if (!(length > 0) || !(width > 0)) return;
          self.state.area = Math.round(length * width * 10) / 10;
          self.syncAreaInput();
          self.render();
        };
        if (this.lengthInput) this.lengthInput.addEventListener('input', recalcArea);
        if (this.widthInput) this.widthInput.addEventListener('input', recalcArea);

        if (this.collectionLink) {
          this.collectionLink.addEventListener('click', function () {
            self.track('bulb_calc_collection_click');
          });
        }

        this.syncSunButtons();
        this.updateHelperLabels();
      }

      /* ---------------- keeping the form in sync with the state ---------------- */

      numberIn(input) {
        if (!input) return NaN;
        var value = parseFloat(input.value);
        return value > 0 ? value : NaN;
      }

      syncAreaInput() {
        if (this.areaInput) this.areaInput.value = this.state.area > 0 ? String(this.state.area) : '';
      }

      /* The four location radios are one set; ?location= picks one of them. */
      syncLocationInputs() {
        var current = this.state.location;
        this.querySelectorAll('[data-bulb-location]').forEach(function (input) {
          input.checked = input.value === current;
        });
      }

      syncSunButtons() {
        var current = this.state.sun;
        this.querySelectorAll('[data-bulb-sun]').forEach(function (button) {
          var on = button.dataset.bulbSun === current;
          button.setAttribute('aria-pressed', on ? 'true' : 'false');
          button.classList.toggle('bulb-calc__pill--on', on);
        });
      }

      updateHelperLabels() {
        var hedge = this.state.location === 'hedge';
        if (this.lengthLabel) {
          this.lengthLabel.textContent = this.t(hedge ? 'hedge_length' : 'length');
        }
        if (this.widthLabel) {
          this.widthLabel.textContent = this.t(hedge ? 'hedge_strip' : 'width');
        }
        if (hedge && this.widthInput && !(this.numberIn(this.widthInput) > 0)) {
          this.widthInput.value = String(this.hedgeStrip);
        }
        this.querySelectorAll('[data-bulb-option]').forEach(function (label) {
          var input = label.querySelector('input');
          label.classList.toggle('bulb-calc__option--selected', !!(input && input.checked));
        });
        this.updateLocationHelp();
        this.updateTips();
      }

      /* The help text of the selected spot, shown as one line under the tiles.
       * Empty while no spot is chosen. */
      updateLocationHelp() {
        if (!this.locationHelp) return;
        var selected = this.state.location
          ? this.querySelector('[data-bulb-option="' + this.state.location + '"]')
          : null;
        this.locationHelp.textContent = selected ? selected.dataset.bulbHelp || '' : '';
      }

      /* Liquid renders all four tip sets plus a prompt, only one of them shows:
       * the prompt while no spot is chosen, otherwise the tips for that spot. */
      updateTips() {
        var current = this.state.location || 'none';
        this.tipBlocks.forEach(function (block) {
          block.hidden = block.dataset.bulbTipsFor !== current;
        });
      }

      /* ---------------- persistence (localStorage, one day) ---------------- */

      loadState() {
        try {
          var raw = window.localStorage.getItem(STORAGE_KEY);
          if (!raw) return null;
          var parsed = JSON.parse(raw);
          if (!parsed || typeof parsed.savedAt !== 'number') return null;
          if (Date.now() - parsed.savedAt > STORAGE_TTL_MS) {
            window.localStorage.removeItem(STORAGE_KEY);
            return null;
          }
          return parsed.state || null;
        } catch (error) {
          return null;
        }
      }

      /* Only known values are taken over; anything odd is ignored. */
      applySavedState(saved) {
        if (LOCATIONS.indexOf(saved.location) !== -1) this.state.location = saved.location;
        if (SUN_LEVELS.indexOf(saved.sun) !== -1) this.state.sun = saved.sun;
        if (saved.area > 0) this.state.area = Number(saved.area);
      }

      saveState() {
        try {
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
            savedAt: Date.now(),
            state: {
              location: this.state.location,
              sun: this.state.sun,
              area: this.state.area,
              length: this.lengthInput ? this.lengthInput.value : '',
              width: this.widthInput ? this.widthInput.value : ''
            }
          }));
        } catch (error) {
          /* Storage can be full or blocked (private mode); the tool works without it. */
        }
      }

      /* "Bloeit maart t/m mei" when the months form one run, otherwise a list.
       * Empty when the product has no flowering months. */
      bloomTextFor(product) {
        var numbers = bloomMonths(product);
        if (!numbers.length) return '';
        var self = this;
        var name = function (n) { return self.t('months.' + n); };
        var contiguous = numbers.every(function (n, index) {
          return index === 0 || n === numbers[index - 1] + 1;
        });
        if (numbers.length >= 2 && contiguous) {
          return this.t('bloom_range', { from: name(numbers[0]), to: name(numbers[numbers.length - 1]) });
        }
        return this.t('bloom_list', { months: numbers.map(name).join(', ') });
      }

      /* ---------------- rendering ---------------- */

      render() {
        var ready = this.isReady();
        this.renderAnswer(ready);
        this.renderSuggestions(ready);
        this.updateHelperLabels();
        this.saveState();
        if (ready && !this.wasReady && this.isNarrow()) this.scrollToAnswer();
        this.wasReady = ready;
      }

      /* The answer is one number: how many bulbs fit in this spot. The quieter
       * number sits under it as a small line. */
      renderAnswer(ready) {
        if (this.answerPrompt) this.answerPrompt.hidden = ready;
        if (this.answerBody) this.answerBody.hidden = !ready;
        if (!ready) {
          if (this.answerHeading) this.answerHeading.textContent = '';
          if (this.answerMin) this.answerMin.textContent = '';
          return;
        }

        var range = answerRange(this.state.location, this.state.area);
        var answerKey = [this.state.location, this.state.sun, this.state.area].join('|');
        if (answerKey !== this.lastAnswerKey) {
          this.lastAnswerKey = answerKey;
          this.track('bulb_calc_answer', { bulb_count: range.max });
        }

        if (this.answerHeading) {
          this.answerHeading.textContent = this.t('answer.heading', {
            area: this.formatArea(this.state.area),
            location: this.t('location_in_sentence.' + this.state.location),
            count: this.formatNumber(range.max)
          });
        }
        if (this.answerMin) {
          this.answerMin.textContent = this.t('answer.min_line', { min: this.formatNumber(range.min) });
        }
      }

      renderSuggestions(ready) {
        if (this.suggestionsEl) this.suggestionsEl.hidden = !ready;
        if (!this.suggestionGrid) return;
        this.suggestionGrid.textContent = '';
        if (!ready) return;

        var self = this;
        var picks = pickSuggestions(this.items, this.state);
        picks.forEach(function (entry, index) {
          self.suggestionGrid.appendChild(self.buildCard(entry, index));
        });
        if (this.emptyState) this.emptyState.hidden = picks.length > 0;
      }

      renderCollectionLink() {
        if (!this.collectionLink) return;
        this.collectionLink.textContent = this.t('suggestions.collection_link', {
          count: this.formatNumber(this.sellableCount)
        });
      }

      /* One suggestion: photo left, the full title, the bloom line, at most two
       * badges, what the customer needs for this spot, the price and one button
       * that puts those packs in the cart. */
      buildCard(entry, index) {
        var self = this;
        var product = entry.item.raw;
        var numbers = entry.numbers;
        var fragment = this.cardTemplate.content.cloneNode(true);
        var card = fragment.querySelector('article');

        card.dataset.handle = product.handle;

        card.querySelectorAll('[data-bulb-card-link], [data-bulb-card-link-title]').forEach(function (link) {
          link.href = product.url;
          link.addEventListener('click', function () {
            self.track('bulb_calc_suggestion_click', Object.assign(
              { bulb_position: index + 1 },
              self.trackItemFor(product.handle)
            ));
          });
        });
        var image = card.querySelector('[data-bulb-card-image]');
        if (product.image) {
          image.src = product.image;
          image.alt = product.image_alt || product.title;
        } else {
          image.remove();
        }
        card.querySelector('[data-bulb-card-title]').textContent = product.title;

        var bloom = card.querySelector('[data-bulb-card-bloom]');
        if (bloom) {
          var bloomText = this.bloomTextFor(product);
          bloom.textContent = bloomText;
          bloom.hidden = !bloomText;
        }

        var badges = card.querySelector('[data-bulb-card-badges]');
        this.badgesFor(entry.item).forEach(function (text) {
          var badge = self.badgeTemplate.content.cloneNode(true).querySelector('span');
          badge.textContent = text;
          badges.appendChild(badge);
        });

        /* Loose bulbs have no pack, so they are counted in bulbs. */
        card.querySelector('[data-bulb-card-need]').textContent = entry.item.loose
          ? this.t('suggestions.for_your_spot_loose', { bulbs: this.formatNumber(numbers.bulbs) })
          : this.t(numbers.packs === 1 ? 'suggestions.for_your_spot_one' : 'suggestions.for_your_spot', {
              packs: this.formatNumber(numbers.packs),
              bulbs: this.formatNumber(numbers.bulbs)
            });

        card.querySelector('[data-bulb-card-total]').textContent = this.formatMoney(numbers.total);
        var unit = card.querySelector('[data-bulb-card-unit]');
        /* The price per pack only says something extra when there is more than
         * one pack. */
        var showUnit = !entry.item.loose && numbers.packs > 1;
        unit.textContent = showUnit
          ? this.t('per_pack', { price: product.price_fmt || this.formatMoney(product.price) })
          : '';
        unit.hidden = !showUnit;

        var addButton = card.querySelector('[data-bulb-add]');
        addButton.textContent = this.t('add_to_cart');
        var errorEl = card.querySelector('[data-bulb-card-error]');
        addButton.addEventListener('click', function () {
          self.addToCart([{ id: product.variant_id, quantity: numbers.packs }], addButton, errorEl, 'suggestion');
        });

        return card;
      }

      /* At most two badges, so a suggestion card stays compact: the spot ones
       * first, then whether the bulbs come back and spread, then shade. */
      badgesFor(item) {
        var badges = [];
        var lawnOk = item.rules.lawn !== null && !(Number(item.raw.height) > LAWN_MAX_HEIGHT);
        if (lawnOk) badges.push(this.t('badge.lawn'));
        if (item.rules.hedge !== null) badges.push(this.t('badge.hedge'));
        if (item.rules.naturalises) badges.push(this.t('badge.naturalises'));
        if (item.rules.spreads) badges.push(this.t('badge.spreads'));
        if (item.lights.indexOf('schaduw') !== -1) badges.push(this.t('badge.shade'));
        return badges.filter(Boolean).slice(0, 2);
      }

      /* ---------------- analytics (Google Tag Manager dataLayer) ---------------- */

      /* Every event carries the form context so GA4 can slice by spot, sun and
       * area. Events are named bulb_calc_*; map them in GTM. */
      trackContext() {
        return {
          bulb_location: this.state.location || '',
          bulb_sun: this.state.sun || '',
          bulb_area: this.state.area > 0 ? this.state.area : 0
        };
      }

      track(name, params) {
        try {
          window.dataLayer = window.dataLayer || [];
          var payload = Object.assign({ event: name, bulb_tool: 'bloembollen-keuzehulp' }, this.trackContext(), params || {});
          window.dataLayer.push(payload);
        } catch (error) {
          /* Analytics must never break the tool. */
        }
      }

      trackItemFor(handle) {
        var item = this.items.filter(function (entry) {
          return entry.raw.handle === handle;
        })[0];
        return item ? { item_id: String(item.raw.variant_id), item_name: item.raw.title, price: Number(item.raw.price) / 100 } : { item_id: handle };
      }

      /* ---------------- cart ---------------- */

      addToCart(items, button, errorEl, source) {
        var self = this;
        var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        var body = {
          items: items,
          sections: cart && typeof cart.getSectionsToRender === 'function'
            ? cart.getSectionsToRender().map(function (section) {
                return section.id;
              }).join(',')
            : '',
          sections_url: window.location.pathname
        };

        if (errorEl) {
          errorEl.hidden = true;
          errorEl.textContent = '';
        }
        var label = button.textContent;
        button.disabled = true;
        button.textContent = this.t('adding');

        fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(body)
        })
          .then(function (response) {
            return response.json();
          })
          .then(function (data) {
            if (data.status) throw data;

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bulb-calculator', cartData: data });
            }

            var trackedItems = items.map(function (line) {
              var match = self.items.filter(function (entry) {
                return String(entry.raw.variant_id) === String(line.id);
              })[0];
              return {
                item_id: String(line.id),
                item_name: match ? match.raw.title : '',
                price: match ? Number(match.raw.price) / 100 : 0,
                quantity: line.quantity
              };
            });
            self.track('bulb_calc_add_to_cart', {
              bulb_source: source || 'suggestion',
              currency: (self.dataset && self.dataset.currency) || 'EUR',
              value: Math.round(trackedItems.reduce(function (acc, line) { return acc + line.price * line.quantity; }, 0) * 100) / 100,
              items: trackedItems
            });

            button.textContent = self.t('added');
            window.setTimeout(function () {
              button.disabled = false;
              button.textContent = label;
            }, 2000);

            if (cart && typeof cart.renderContents === 'function') {
              cart.classList.remove('is-empty');
              cart.renderContents(data);
            } else {
              window.location = window.routes.cart_url;
            }
          })
          .catch(function (error) {
            console.error('[bulb-calculator] add to cart failed', error);
            button.disabled = false;
            button.textContent = label;
            if (errorEl) {
              errorEl.textContent = self.t('cart_error');
              errorEl.hidden = false;
            }
          });
      }
    }

    if (!customElements.get('bulb-calculator')) {
      customElements.define('bulb-calculator', BulbCalculator);
    }
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      BULB_RULES: BULB_RULES,
      GENUS_PATTERNS: GENUS_PATTERNS,
      LATIN_GENUS: LATIN_GENUS,
      POPULARITY: POPULARITY,
      classify: classify,
      answerRange: answerRange,
      densityFor: densityFor,
      bloomMonths: bloomMonths,
      LOCATION_GUIDE: LOCATION_GUIDE,
      LOOSE_FACTOR: LOOSE_FACTOR,
      SUGGESTION_COUNT: SUGGESTION_COUNT,
      rulesForProduct: rulesForProduct,
      mergeRules: mergeRules,
      ruleForKey: ruleForKey,
      parsePackSize: parsePackSize,
      lightLevelsOf: lightLevelsOf,
      seasonOf: seasonOf,
      enrich: enrich,
      exclusionReason: exclusionReason,
      computeItem: computeItem,
      groupKeyFor: groupKeyFor,
      suggestionScore: suggestionScore,
      pickSuggestions: pickSuggestions,
      translate: translate,
      matchGenera: matchGenera,
      introHead: introHead,
      DENSITY_FIELD: DENSITY_FIELD
    };
  }
})();
